import { SearchTextField } from "@components/common/search-text-field";
import { SEARCHBAR_CONFIG, SearchbarErrorMsg } from "@components/header/searchbar-utils";
import { useDebouncedSearchText } from "@hooks/use-debounced-search-text";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useRouteMatch } from "@hooks/use-route-match";
import { Box } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { resetSearchText, setDebouncedSearchText, setIsDebouncingSearch } from "@store/ui/ui-slice";
import { searchSelector } from "@store/ui/ui-selector";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { onClearTableSearch } from "@components/common/faro-table/faro-table-filter/filter-toolbar-quick-search";

/**
 * Width in pixels of the search bar: wide enough for the longest placeholder, "Search for
 * workspace", which needs about 203px with the padding and the search icon.
 */
const SEARCHBAR_WIDTH = 224;

/** On narrow screens, reduce the width so that there's enough space e.g. for the breadcrumb menu. */
const SEARCHBAR_WIDTH_SMALL = 100;

/** Default placeholder text for the search bar when not provided or on small screens */
const DEFAULT_SEARCHBAR_PLACEHOLDER = "Search";
/** Search bar component that automatically changes its context depending on the current route */
export function Searchbar({
  shouldFullWidth = false,
  shouldSkipValidation = false,
  variant
}) {
  const dispatch = useAppDispatch();
  const {
    pathname
  } = useLocation();
  const routeIdentifier = useRouteMatch();
  const {
    debouncedSearchText: debouncedSearchTextFromRedux
  } = useAppSelector(searchSelector);
  const [searchText, setSearchText] = useState("");
  const {
    trackEvent
  } = useTrackEvent();
  const {
    debouncedSearchText,
    isDebouncingSearch
  } = useDebouncedSearchText({
    searchText
  });
  const {
    isScreenXs,
    isScreenSmOrSmaller
  } = useMediaQueryList();

  /** Whether to use the smaller search bar, with the default placeholder to save space */
  const shouldUseSmallerSearchbar = isScreenXs;

  /** The placeholder to be used. On smaller screens only the default one is shown */
  const placeholder = shouldUseSmallerSearchbar || !SEARCHBAR_CONFIG[routeIdentifier].placeholder ? DEFAULT_SEARCHBAR_PLACEHOLDER : SEARCHBAR_CONFIG[routeIdentifier].placeholder;

  /** The width of the search bar depending on the screen size */
  const searchBarWidth = shouldUseSmallerSearchbar ? SEARCHBAR_WIDTH_SMALL : SEARCHBAR_WIDTH;

  /** Whether the search value is valid */
  const isValid = useMemo(() => {
    if (shouldSkipValidation) {
      return true;
    }
    const validator = SEARCHBAR_CONFIG[routeIdentifier].validator;
    return validator ? validator(debouncedSearchText) : true;
  }, [debouncedSearchText, routeIdentifier, shouldSkipValidation]);

  /** Sync local state with Redux value on mount and screen size change */
  useEffect(() => {
    setSearchText(debouncedSearchTextFromRedux || "");
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScreenSmOrSmaller]);

  /** Updates the debounced search text value in the redux store for every change */
  useEffect(() => {
    dispatch(setDebouncedSearchText(debouncedSearchText));
  }, [debouncedSearchText, dispatch]);

  /** Updates the isDebouncingSearch flag value in the redux store for every change */
  useEffect(() => {
    dispatch(setIsDebouncingSearch(isDebouncingSearch));
  }, [dispatch, isDebouncingSearch]);

  /** Clears the search text and resets the debounced value in redux store */
  const clearSearchText = useCallback(() => {
    setSearchText("");
    dispatch(setDebouncedSearchText(""));
  }, [dispatch]);

  /**
   * Resets the searchText when the route/page changes.
   * This runs after the new page has rendered, so the page is told which pathname the search
   * belongs to, letting it ignore the text typed on the previous page until then.
   */
  useEffect(() => {
    if (pathname) {
      setSearchText("");
      dispatch(resetSearchText(pathname));
    }
  }, [pathname, dispatch]);

  /** Clear the search text when the event is emitted from another component */
  useEffect(() => {
    const disposer = onClearTableSearch.on(clearSearchText);
    return () => disposer.dispose();
  }, [clearSearchText]);

  /**
   * Tracks the search event if:
   * - The log event params are defined and
   * - The debounced search value changes and
   * - The user is not typing.
   */
  useEffect(() => {
    const logEventParams = SEARCHBAR_CONFIG[routeIdentifier].logEventParams;
    if (logEventParams && debouncedSearchText.length > 0 && !isDebouncingSearch) {
      // Include the amount of typed characters in the tracked search event.
      trackEvent({
        ...logEventParams,
        props: {
          ...logEventParams.props,
          textLength: debouncedSearchText.length
        }
      });
    }
  }, [debouncedSearchText.length, isDebouncingSearch, routeIdentifier, trackEvent]);
  if (!SEARCHBAR_CONFIG[routeIdentifier].isSearchAllowed) {
    return;
  }
  return <Box component="div" sx={{
    display: "flex",
    alignItems: "center"
  }}>
      {/* Search field */}
      <Box component="div" sx={{
      minWidth: "100px",
      width: shouldFullWidth ? "100%" : `${searchBarWidth}px`,
      marginRight: shouldFullWidth ? undefined : "0.75rem"
    }}>
        <SearchTextField placeholder={placeholder} searchText={searchText} onClearClicked={() => setSearchText("")} onChangeSearch={setSearchText} isError={!isValid} variant={variant} />
        {/* Error helper text that is shown on the bottom of the search field for mobile screens */}
        {!isValid && <SearchbarErrorMsg errorMessage={SEARCHBAR_CONFIG[routeIdentifier].errorMessage} />}
      </Box>
    </Box>;
}